import { useCallback, useEffect, useMemo, useState } from "react";
import Toast from "components/ui/tableComponents/Toast";
import {
  COMMISSION_PERCENT_DEFAULTS,
  getConfig,
  updateCommissionSettings,
} from "services/config.service";
import { notifyCommissionUpdated } from "utils/commissionEvents";

const FIELDS = [
  {
    key: "fcMotivatePercentage",
    label: "FC Motivate",
  },
  {
    key: "emailManagerPercentage",
    label: "Email Manager",
  },
  {
    key: "acePerformancePercentage",
    label: "ACE Performance",
  },
  {
    key: "saleTeamPercentage",
    label: "Sale Team",
  },
  {
    key: "saleTeam5kPercentage",
    label: "Sale Team ($5K)",
  },
];

const toFormState = (config) =>
  Object.fromEntries(
    FIELDS.map(({ key }) => [key, String(config[key] ?? COMMISSION_PERCENT_DEFAULTS[key])])
  );

const validatePercent = (value) => {
  if (value === "" || value === null || value === undefined) {
    return "Percentage is required";
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return "Enter a valid number";
  if (num < 0 || num > 100) return "Percentage must be between 0 and 100";
  return "";
};

export default function CommissionSettingsPanel({ api, onSaved }) {
  const [form, setForm] = useState(() => toFormState(COMMISSION_PERCENT_DEFAULTS));
  const [saved, setSaved] = useState(COMMISSION_PERCENT_DEFAULTS);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const loadData = useCallback(async () => {
    const config = await getConfig(api);
    setForm(toFormState(config));
    setSaved(config);
    setErrors({});
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        await loadData();
      } catch {
        if (!cancelled) {
          setLoadError("Failed to load commission settings. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [loadData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: validatePercent(value) }));
  };

  const hasErrors = useMemo(
    () => FIELDS.some(({ key }) => !!validatePercent(form[key])),
    [form]
  );

  const isDirty = useMemo(
    () => FIELDS.some(({ key }) => Number(form[key]) !== Number(saved[key])),
    [form, saved]
  );

  const handleCancel = () => {
    setForm(toFormState(saved));
    setErrors({});
  };

  const handleSave = async () => {
    const nextErrors = {};
    FIELDS.forEach(({ key }) => {
      const error = validatePercent(form[key]);
      if (error) nextErrors[key] = error;
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = Object.fromEntries(
      FIELDS.map(({ key }) => [key, Number(form[key])])
    );

    setIsSaving(true);
    try {
      const response = await updateCommissionSettings(api, payload);
      const next = response.data;
      setForm(toFormState(next));
      setSaved(next);
      onSaved?.(next);
      notifyCommissionUpdated({ type: "config" });
      setToast({
        show: true,
        message: "Commission settings saved successfully",
        type: "success",
      });
    } catch {
      setToast({
        show: true,
        message: "Failed to save commission settings",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isDisabled = isLoading || isSaving;

  return (
    <div className="mb-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Commission Settings</h2>
          <p className="mt-1 text-sm text-gray-500">
            AppConfig percentages apply to new assignments only. Existing sales keep
            their stored commission percent.
          </p>
        </div>

        <div className="p-4">
          {loadError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {loadError}
            </div>
          )}

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {FIELDS.map(({ key }) => (
                <div key={key}>
                  <div className="h-4 w-36 bg-gray-200 animate-pulse rounded" />
                  <div className="h-10 w-full bg-gray-200 animate-pulse rounded mt-2" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <label
                      htmlFor={`commission-${key}`}
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {label} (%)
                    </label>
                    <div className="relative">
                      <input
                        id={`commission-${key}`}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={form[key]}
                        onChange={(e) => handleChange(key, e.target.value)}
                        disabled={isDisabled}
                        className={`w-full rounded-lg border px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-500 ${
                          errors[key]
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                        %
                      </span>
                    </div>
                    {errors[key] && (
                      <p className="mt-1 text-sm text-red-600">{errors[key]}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isDisabled || !isDirty}
                  className="px-6 py-2 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 shadow-sm hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isDisabled || hasErrors || !isDirty}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}
